const GOOGLE_PLACES_SCRIPT_ID = 'litium-google-places';
const GOOGLE_PLACES_CALLBACK_NAME = '__litiumGooglePlacesReady';

let googlePlacesScriptPromise = null;
let googlePlacesLibraryPromise = null;
const scopeCleanupMap = new WeakMap();

const VALUE_SYNC_EVENTS = ['input', 'change', 'keyup', 'blur'];

const shouldBypassGooglePlacesForCurrentHost = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    const hostName = (window.location?.hostname || '')
        .trim()
        .toLowerCase();

    return (
        hostName === 'localhost' ||
        hostName === '127.0.0.1' ||
        hostName === '::1' ||
        hostName.endsWith('.localtest.me')
    );
};

const hasGoogleMapsLoader = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    return !!(
        window.google &&
        window.google.maps &&
        typeof window.google.maps.importLibrary === 'function'
    );
};

export const hasGooglePlaces = () => {
    return hasGoogleMapsLoader();
};

export const getGooglePlacesApiKey = () => {
    return (
        window.__litium?.checkoutGoogleMapsApiKey ||
        window.__litium?.googleMapsApiKey ||
        ''
    );
};

const normalizeCountryCode = (countryCode) => {
    if (!countryCode || typeof countryCode !== 'string') {
        return '';
    }

    return countryCode.trim().toLowerCase();
};

const getDocumentLanguage = () => {
    if (typeof document === 'undefined') {
        return '';
    }

    return (
        document.documentElement?.lang ||
        navigator.language ||
        ''
    ).trim();
};

const cleanupGooglePlacesCallback = () => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        delete window[GOOGLE_PLACES_CALLBACK_NAME];
    } catch (error) {
        window[GOOGLE_PLACES_CALLBACK_NAME] = undefined;
    }
};

export const loadGooglePlacesScript = (apiKey = getGooglePlacesApiKey()) => {
    if (typeof window === 'undefined') {
        return Promise.resolve(false);
    }

    if (shouldBypassGooglePlacesForCurrentHost()) {
        return Promise.resolve(false);
    }

    if (hasGoogleMapsLoader()) {
        return Promise.resolve(true);
    }

    if (!apiKey) {
        return Promise.resolve(false);
    }

    if (googlePlacesScriptPromise) {
        return googlePlacesScriptPromise;
    }

    googlePlacesScriptPromise = new Promise((resolve) => {
        const resolveLoad = (isLoaded) => {
            cleanupGooglePlacesCallback();
            resolve(isLoaded);
        };

        const rejectLoad = () => {
            cleanupGooglePlacesCallback();
            googlePlacesScriptPromise = null;
            googlePlacesLibraryPromise = null;
            resolve(false);
        };

        window[GOOGLE_PLACES_CALLBACK_NAME] = () => {
            resolveLoad(hasGoogleMapsLoader());
        };

        const existingScript = document.getElementById(GOOGLE_PLACES_SCRIPT_ID);
        if (existingScript) {
            if (hasGoogleMapsLoader()) {
                resolveLoad(true);
                return;
            }

            existingScript.addEventListener(
                'load',
                () => resolveLoad(hasGoogleMapsLoader()),
                { once: true }
            );
            existingScript.addEventListener('error', rejectLoad, {
                once: true,
            });
            return;
        }

        const script = document.createElement('script');
        script.id = GOOGLE_PLACES_SCRIPT_ID;
        script.async = true;
        script.defer = true;
        script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
            apiKey
        )}&libraries=places&loading=async&callback=${GOOGLE_PLACES_CALLBACK_NAME}`;
        script.onload = () => {
            if (hasGoogleMapsLoader()) {
                resolveLoad(true);
            }
        };
        script.onerror = rejectLoad;
        document.head.appendChild(script);
    });

    return googlePlacesScriptPromise;
};

const loadGooglePlacesLibrary = async (
    apiKey = getGooglePlacesApiKey()
) => {
    if (typeof window === 'undefined') {
        return null;
    }

    if (googlePlacesLibraryPromise) {
        return googlePlacesLibraryPromise;
    }

    googlePlacesLibraryPromise = (async () => {
        const isLoaded = await loadGooglePlacesScript(apiKey);
        if (!isLoaded || !hasGoogleMapsLoader()) {
            googlePlacesLibraryPromise = null;
            return null;
        }

        try {
            const placesLibrary = await window.google.maps.importLibrary(
                'places'
            );
            if (!placesLibrary?.PlaceAutocompleteElement) {
                googlePlacesLibraryPromise = null;
                return null;
            }

            return placesLibrary;
        } catch (error) {
            googlePlacesLibraryPromise = null;
            return null;
        }
    })();

    return googlePlacesLibraryPromise;
};

const getPlaceComponents = (place) => {
    if (!place) {
        return [];
    }

    return place.addressComponents || place.address_components || [];
};

const getFormattedAddress = (place) => {
    return place?.formattedAddress || place?.formatted_address || '';
};

const getAddressComponent = (components, type, shortName = false) => {
    const component = (components || []).find(
        (item) => item && Array.isArray(item.types) && item.types.includes(type)
    );

    if (!component) {
        return '';
    }

    if (shortName) {
        return (
            component.shortText ||
            component.short_name ||
            component.short_name_text ||
            ''
        );
    }

    return (
        component.longText ||
        component.long_name ||
        component.long_name_text ||
        ''
    );
};

export const mapPlaceToAddress = (place) => {
    const components = getPlaceComponents(place);
    const streetNumber = getAddressComponent(components, 'street_number');
    const route = getAddressComponent(components, 'route');
    const postalCode = getAddressComponent(components, 'postal_code');
    const locality =
        getAddressComponent(components, 'locality') ||
        getAddressComponent(components, 'postal_town') ||
        getAddressComponent(components, 'sublocality') ||
        getAddressComponent(components, 'administrative_area_level_2');
    const state = getAddressComponent(
        components,
        'administrative_area_level_1',
        true
    );
    const country = getAddressComponent(components, 'country', true);
    const line1 = [route, streetNumber].filter((value) => !!value).join(' ');

    return {
        address: line1 || getFormattedAddress(place) || '',
        zipCode: postalCode,
        city: locality,
        state,
        country,
    };
};

const setFieldValue = (element, value, shouldDispatch = true) => {
    if (!element || !value) {
        return;
    }

    if (
        element.tagName === 'SELECT' &&
        !Array.from(element.options || []).some(
            (option) =>
                (option.value || '').toUpperCase() === value.toUpperCase()
        )
    ) {
        return;
    }

    if (element.value === value) {
        return;
    }

    element.value = value;
    if (shouldDispatch) {
        element.dispatchEvent(new Event('input', { bubbles: true }));
        element.dispatchEvent(new Event('change', { bubbles: true }));
    }
};

const extractSelectedPlace = (event) => {
    return (
        event?.placePrediction ||
        event?.place ||
        event?.detail?.placePrediction ||
        event?.detail?.place ||
        null
    );
};

const createCleanupBag = () => {
    const handlers = [];

    return {
        add: (handler) => {
            if (typeof handler === 'function') {
                handlers.push(handler);
            }
        },
        run: () => {
            handlers.splice(0).forEach((handler) => {
                try {
                    handler();
                } catch (error) {
                    // Ignore cleanup errors.
                }
            });
        },
    };
};

const bindDomEvent = (target, eventName, handler, cleanupBag) => {
    if (!target || typeof target.addEventListener !== 'function') {
        return;
    }

    target.addEventListener(eventName, handler);
    cleanupBag.add(() => target.removeEventListener(eventName, handler));
};

const bindValueSync = (target, handler, cleanupBag) => {
    VALUE_SYNC_EVENTS.forEach((eventName) => {
        bindDomEvent(target, eventName, handler, cleanupBag);
    });
};

const findAssociatedLabel = (inputElement) => {
    if (!inputElement || !inputElement.id || typeof document === 'undefined') {
        return null;
    }

    return document.querySelector(`label[for="${inputElement.id}"]`);
};

const hideOriginalInput = (inputElement) => {
    if (!inputElement) {
        return;
    }

    if (!inputElement.dataset.googlePlacesOriginalDisplay) {
        inputElement.dataset.googlePlacesOriginalDisplay = inputElement.style.display || '';
    }

    inputElement.classList.add('litium-google-places-source-input');
    inputElement.setAttribute('aria-hidden', 'true');
    inputElement.tabIndex = -1;
    inputElement.style.display = 'none';
};

const restoreOriginalInput = (inputElement) => {
    if (!inputElement) {
        return;
    }

    inputElement.classList.remove('litium-google-places-source-input');
    inputElement.removeAttribute('aria-hidden');
    inputElement.removeAttribute('tabindex');
    inputElement.style.display =
        inputElement.dataset.googlePlacesOriginalDisplay || '';
    delete inputElement.dataset.googlePlacesOriginalDisplay;
};

const configureAutocompleteElement = ({
    autocompleteElement,
    inputElement,
    getCountryCode,
}) => {
    autocompleteElement.className = [
        inputElement.className || '',
        'litium-google-places-autocomplete',
    ]
        .join(' ')
        .trim();

    if (inputElement.id) {
        autocompleteElement.id = `${inputElement.id}__googlePlaces`;
    }

    if (inputElement.placeholder) {
        autocompleteElement.setAttribute(
            'placeholder',
            inputElement.placeholder
        );
    }

    const label = findAssociatedLabel(inputElement);
    const ariaLabel =
        inputElement.getAttribute('aria-label') ||
        label?.textContent?.trim() ||
        inputElement.getAttribute('placeholder') ||
        '';
    if (ariaLabel) {
        autocompleteElement.setAttribute('aria-label', ariaLabel);
    }

    const documentLanguage = getDocumentLanguage();
    if ('requestedLanguage' in autocompleteElement && documentLanguage) {
        autocompleteElement.requestedLanguage = documentLanguage;
    }

    if (inputElement.disabled) {
        autocompleteElement.setAttribute('disabled', '');
    }

    autocompleteElement.value = inputElement.value || '';

    const countryCode =
        typeof getCountryCode === 'function' ? getCountryCode() : '';
    autocompleteElement.includedRegionCodes = countryCode
        ? [normalizeCountryCode(countryCode)]
        : [];
};

const attachInternalInputSync = (
    autocompleteElement,
    handler,
    cleanupBag
) => {
    let frameCount = 0;
    let stopped = false;

    const attach = () => {
        if (stopped || !autocompleteElement?.shadowRoot) {
            if (stopped || frameCount > 12) {
                return;
            }

            frameCount += 1;
            window.requestAnimationFrame(attach);
            return;
        }

        const internalInput = autocompleteElement.shadowRoot.querySelector(
            'input'
        );
        if (!internalInput) {
            if (frameCount > 12) {
                return;
            }

            frameCount += 1;
            window.requestAnimationFrame(attach);
            return;
        }

        bindValueSync(internalInput, handler, cleanupBag);
    };

    window.requestAnimationFrame(attach);
    cleanupBag.add(() => {
        stopped = true;
    });
};

export const createAddressAutocomplete = ({
    inputElement,
    getCountryCode = null,
    onResolvedAddress = null,
}) => {
    let autocompleteElement = null;
    let destroyed = false;
    const cleanupBag = createCleanupBag();

    const syncInputFromAutocomplete = (shouldDispatch = true) => {
        if (!autocompleteElement || !inputElement) {
            return;
        }

        const nextValue = autocompleteElement.value || '';
        if (inputElement.value === nextValue) {
            return;
        }

        inputElement.value = nextValue;
        if (shouldDispatch) {
            inputElement.dispatchEvent(new Event('input', { bubbles: true }));
            inputElement.dispatchEvent(new Event('change', { bubbles: true }));
        }
    };

    const syncAutocompleteFromInput = () => {
        if (!autocompleteElement || !inputElement) {
            return;
        }

        const nextValue = inputElement.value || '';
        if (autocompleteElement.value === nextValue) {
            return;
        }

        autocompleteElement.value = nextValue;
    };

    const refreshCountryRestriction = () => {
        if (!autocompleteElement) {
            return;
        }

        const countryCode =
            typeof getCountryCode === 'function' ? getCountryCode() : '';
        autocompleteElement.includedRegionCodes = countryCode
            ? [normalizeCountryCode(countryCode)]
            : [];
    };

    const handlePlaceSelection = async (event) => {
        if (destroyed) {
            return;
        }

        const selectedPlace = extractSelectedPlace(event);
        const place =
            typeof selectedPlace?.toPlace === 'function'
                ? selectedPlace.toPlace()
                : selectedPlace;

        if (!place) {
            syncInputFromAutocomplete(true);
            return;
        }

        if (typeof place.fetchFields === 'function') {
            try {
                await place.fetchFields({
                    fields: ['addressComponents', 'formattedAddress'],
                });
            } catch (error) {
                // Ignore field fetch failures and keep the current widget value.
            }
        }

        const parsedAddress = mapPlaceToAddress(place);
        const resolvedAddress =
            parsedAddress.address ||
            getFormattedAddress(place) ||
            autocompleteElement?.value ||
            '';

        if (resolvedAddress && autocompleteElement) {
            autocompleteElement.value = resolvedAddress;
        }

        syncInputFromAutocomplete(true);

        if (typeof onResolvedAddress === 'function') {
            onResolvedAddress(parsedAddress, place);
        }
    };

    const init = async () => {
        if (destroyed || !inputElement) {
            return false;
        }

        const placesLibrary = await loadGooglePlacesLibrary();
        if (
            destroyed ||
            !placesLibrary?.PlaceAutocompleteElement ||
            !inputElement
        ) {
            return false;
        }

        autocompleteElement = new placesLibrary.PlaceAutocompleteElement();
        configureAutocompleteElement({
            autocompleteElement,
            inputElement,
            getCountryCode,
        });

        bindValueSync(autocompleteElement, () => {
            syncInputFromAutocomplete(true);
        }, cleanupBag);
        bindDomEvent(
            autocompleteElement,
            'gmp-select',
            handlePlaceSelection,
            cleanupBag
        );
        bindDomEvent(
            autocompleteElement,
            'gmp-placeselect',
            handlePlaceSelection,
            cleanupBag
        );

        bindValueSync(inputElement, () => {
            syncAutocompleteFromInput();
        }, cleanupBag);
        attachInternalInputSync(
            autocompleteElement,
            () => syncInputFromAutocomplete(true),
            cleanupBag
        );

        const label = findAssociatedLabel(inputElement);
        if (label) {
            bindDomEvent(
                label,
                'click',
                (event) => {
                    event.preventDefault();
                    autocompleteElement?.focus();
                },
                cleanupBag
            );
        }

        inputElement.parentNode?.insertBefore(autocompleteElement, inputElement);
        hideOriginalInput(inputElement);
        refreshCountryRestriction();
        syncAutocompleteFromInput();
        return true;
    };

    const destroy = () => {
        destroyed = true;
        cleanupBag.run();
        autocompleteElement?.remove();
        autocompleteElement = null;
        restoreOriginalInput(inputElement);
    };

    return {
        init,
        destroy,
        refreshCountryRestriction,
        getAutocomplete: () => autocompleteElement,
    };
};

export const initAddressAutocompleteScope = (scopeElement) => {
    if (!scopeElement || scopeCleanupMap.has(scopeElement)) {
        return scopeCleanupMap.get(scopeElement) || null;
    }

    const addressInput = scopeElement.querySelector(
        '[data-google-places-field="address"]'
    );
    if (!addressInput) {
        return null;
    }

    const zipInput = scopeElement.querySelector(
        '[data-google-places-field="zipCode"]'
    );
    const cityInput = scopeElement.querySelector(
        '[data-google-places-field="city"]'
    );
    const stateInput = scopeElement.querySelector(
        '[data-google-places-field="state"]'
    );
    const countryInput = scopeElement.querySelector(
        '[data-google-places-field="country"]'
    );

    let controller = null;

    const createController = () =>
        createAddressAutocomplete({
            inputElement: addressInput,
            getCountryCode: () => countryInput?.value || '',
            onResolvedAddress: (parsedAddress) => {
                setFieldValue(addressInput, parsedAddress.address);
                setFieldValue(zipInput, parsedAddress.zipCode);
                setFieldValue(cityInput, parsedAddress.city);
                setFieldValue(stateInput, parsedAddress.state);
                setFieldValue(
                    countryInput,
                    parsedAddress.country
                        ? parsedAddress.country.toUpperCase()
                        : ''
                );
            },
        });

    const initController = () => {
        controller = createController();
        controller.init();
    };

    const destroyController = () => {
        controller?.destroy();
        controller = null;
    };

    const handleCountryChange = () => {
        // Recreate the Google Places element to ensure region restrictions
        // actually follow the selected country across all browsers/widgets.
        destroyController();
        initController();
    };

    initController();
    countryInput?.addEventListener('change', handleCountryChange);

    const cleanup = () => {
        countryInput?.removeEventListener('change', handleCountryChange);
        destroyController();
        scopeCleanupMap.delete(scopeElement);
    };

    scopeCleanupMap.set(scopeElement, cleanup);
    return cleanup;
};

export const initMyPageAddressAutocomplete = () => {
    if (typeof document === 'undefined') {
        return;
    }

    document
        .querySelectorAll('[data-google-places-scope]')
        .forEach((scopeElement) => {
            initAddressAutocompleteScope(scopeElement);
        });
};
